import { mapTokensToPalette } from "./token-adaptor";
import type {
	ColorSystemOptions,
	PaletteColorOptions,
} from "@mui/material/styles";

import type { ColorScheme } from "./types";
import type { Brand } from "@/types/brand.types";

declare module "@mui/material/styles" {
	interface PaletteColor {
		activated?: string;
		hovered?: string;
		selected?: string;
	}

	interface SimplePaletteColorOptions {
		activated?: string;
		hovered?: string;
		selected?: string;
	}

	interface TypeBackground {
		level1: string;
		level2: string;
		level3: string;
	}

	interface PaletteOptions {
		neutral?: PaletteColorOptions;
		shadow?: string;
		Avatar?: { defaultBg?: string };
		Backdrop?: { bg?: string };
		OutlinedInput?: { border?: string };
		TableCell?: { border?: string };
		Tooltip?: { bg?: string };
	}
}

export function colorSchemes(
	brand: Brand
): Partial<Record<ColorScheme, ColorSystemOptions>> {
	const lightPalette = mapTokensToPalette(brand, "light");
	const darkPalette = mapTokensToPalette(brand, "dark");

	// BattMobility B2B palette (light mode).
	// Overlays B2B brand colors on top of Moby tokens, preserving semantic
	// colors (error/warning/info/success) and brand shades.
	if (lightPalette.primary) {
		lightPalette.primary = {
			...lightPalette.primary,
			main: "#3D73F0", // Future Blue
			hovered: "#1F4AB0", // clearly darker blue on hover
			activated: "#163A8E",
			selected: "#DBE5FB", // pale primary tint for selected rows
			contrastText: "#FFFFFF",
		};
	}
	if (lightPalette.secondary) {
		lightPalette.secondary = {
			...lightPalette.secondary,
			main: "#D1E8D1", // Eco Horizon
			contrastText: "#4F4F4F", // Graphite
		};
	}
	if (lightPalette.error) {
		lightPalette.error = {
			...lightPalette.error,
			hovered: "#B71C1C", // strong dark red
			activated: "#8B0000",
			contrastText: "#FFFFFF",
		};
	}
	if (lightPalette.success) {
		lightPalette.success = {
			...lightPalette.success,
			hovered: "#1B5E20", // strong dark green
			activated: "#0E3F12",
			contrastText: "#FFFFFF",
		};
	}
	if (lightPalette.background) {
		// Off White is the page bg; level1/2/3 stay neutral near-white so the many
		// components that opt into them (banners, accordions, card sections) render
		// on a consistent subtle surface instead of a branded accent.
		lightPalette.background = {
			...lightPalette.background,
			default: "#F6F9FD", // Off White
			paper: "#FFFFFF",
			level1: "#F0F2F6", // subtle neutral surface
			level2: "#E6EAF0",
			level3: "#DCE1E8",
		};
	}
	lightPalette.text = {
		primary: "#4F4F4F", // Graphite Drive
		secondary: "#717171",
		disabled: "#D8D8D8", // Urban Mist (B2B)
	};
	lightPalette.divider = "rgba(79, 79, 79, 0.12)";

	return {
		dark: {
			palette: darkPalette,
		},
		light: {
			palette: lightPalette,
		},
	};
}
